import React, { Fragment, useEffect } from 'react';
import { DashboardSkeleton } from 'pages/ReportsPage/components/AnalyticsDashboards/Skeleton';
import { useTranslation } from 'hooks/useTranslation';
import { useAnalyticsFilterStore } from 'pages/ReportsPage/store/analyticsFilterView.context';
import { useDailyOvertimeDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/DailyOvertimeDashboard/store/dailyOvertimeDashboard.store';
import { useDailyOvertimeKpiCards } from 'pages/ReportsPage/components/AnalyticsDashboards/DailyOvertimeDashboard/useDailyOvertimeKpiCards';
import { KpiStatCardRow } from 'pages/ReportsPage/components/AnalyticsDashboards/KpiStatCardRow';
import { FilterBar } from 'pages/ReportsPage/components/AnalyticsDashboards/FilterBar';
import { WidgetCardWrapper } from 'pages/ReportsPage/components/AnalyticsDashboards/WidgetCardWrapper';
import { OtByPositionChart } from 'pages/ReportsPage/components/AnalyticsDashboards/DailyOvertimeDashboard/OtByPositionChart';
import { OtByTeamMemberTypeChart } from 'pages/ReportsPage/components/AnalyticsDashboards/DailyOvertimeDashboard/OtByTeamMemberTypeChart';
import { OtTrendChart } from 'pages/ReportsPage/components/AnalyticsDashboards/DailyOvertimeDashboard/OtTrendChart';
import { TopOvertimeIndividualsTable } from 'pages/ReportsPage/components/AnalyticsDashboards/DailyOvertimeDashboard/TopOvertimeIndividualsTable';
import {
  DashboardContainer,
  DashboardHeader,
  DashboardContent,
  DashboardTitle,
  DashboardErrorContainer,
  WidgetGrid,
  FullWidthSection,
} from 'pages/ReportsPage/components/AnalyticsDashboards/DailyOvertimeDashboard/DailyOvertimeDashboard.styled';

export const DailyOvertimeDashboard: React.FC = () => {
  const { t } = useTranslation();
  const fetchData = useDailyOvertimeDashboardStore((s) => s.fetchData);
  const reset = useDailyOvertimeDashboardStore((s) => s.reset);
  const isLoading = useDailyOvertimeDashboardStore((s) => s.isLoading);
  const error = useDailyOvertimeDashboardStore((s) => s.error);
  const dateRange = useAnalyticsFilterStore((s) => s.dateRange);
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const employmentTypes = useAnalyticsFilterStore((s) => s.employmentTypes);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const kpiCards = useDailyOvertimeKpiCards();

  useEffect(() => {
    return fetchData(dateRange, locationIds, positions, employmentTypes);
  }, [fetchData, dateRange, locationIds, employmentTypes, positions]);

  useEffect(() => {
    return () => reset();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DashboardContainer aria-label={t('reports:DAILY_OVERTIME_DASHBOARD_TITLE')}>
      <DashboardHeader>
        <DashboardTitle>{t('reports:DAILY_OVERTIME_DASHBOARD_TITLE')}</DashboardTitle>
      </DashboardHeader>
      <FilterBar />
      <DashboardContent>
        {error ? (
          <div aria-live="assertive">
            <DashboardErrorContainer>{error}</DashboardErrorContainer>
          </div>
        ) : isLoading ? (
          <DashboardSkeleton kpiCount={2} widgetCount={4} />
        ) : (
          <Fragment>
            <KpiStatCardRow cards={kpiCards} fullWidth />
            <WidgetGrid>
              <WidgetCardWrapper
                title={t('reports:OVERTIME_BY_POSITION_TITLE')}
                subtitle={t('reports:OVERTIME_BY_POSITION_SUBTITLE')}
              >
                <OtByPositionChart />
              </WidgetCardWrapper>
              <WidgetCardWrapper
                title={t('reports:OVERTIME_BY_TYPE_TITLE')}
                subtitle={t('reports:OVERTIME_BY_TYPE_SUBTITLE')}
              >
                <OtByTeamMemberTypeChart />
              </WidgetCardWrapper>
            </WidgetGrid>
            <FullWidthSection>
              <WidgetCardWrapper
                title={t('reports:DAILY_OVERTIME_TREND_TITLE')}
                subtitle={t('reports:DAILY_OVERTIME_TREND_SUBTITLE')}
              >
                <OtTrendChart />
              </WidgetCardWrapper>
            </FullWidthSection>
            <FullWidthSection>
              <WidgetCardWrapper
                title={t('reports:DAILY_OVERTIME_INDIVIDUALS_TITLE')}
                subtitle={t('reports:DAILY_OVERTIME_INDIVIDUALS_SUBTITLE')}
              >
                <TopOvertimeIndividualsTable />
              </WidgetCardWrapper>
            </FullWidthSection>
          </Fragment>
        )}
      </DashboardContent>
    </DashboardContainer>
  );
};
